import React, { useState, useCallback, useEffect } from 'react';
import type { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Transmit,
  getAuthParams,
  handleAuthRedirects,
  TRANSMIT_CONSTANTS,
} from './TransmitSdk';
import {
  CustomerVerificationFormData,
  TransmitResponse,
} from '../../interfaces/CustomerVerification.interface';
import { getCustomerVerificationContent } from '../../store/contentSlice';
import {
  addFormData,
  setTransmitToken,
  setPiiFormEvent,
  getPiiFormData,
} from '../../store/customerVerificationFormSlice';
import { getSessionId, getClientId } from '../../store/sessionSlice';
import { useAppDispatch } from '../../store/store';
import { CLIENT_ID_SESSION_STORAGE_KEY } from '../../utils/constants/sessionStorageConstants';
import { detectDeviceType } from '../../utils/helpers';
import {
  capturePageLoadAnalytics,
  captureClickAnalyticsObject,
  captureAnalyticsDataAttributes,
} from '../../utils/analytics/analyticsDispatchers';
import { PII_PAGE_LOAD_EVENT } from '../../utils/analytics/analyticsEvents';
import CancelModal from './CancelModal';
import './customerVerification.css';

const CustomerVerification: FunctionComponent = () => {
  const {
    header,
    subHeader,
    disclaimerText,
    lastFourSocialErrorText,
    phoneNumberErrorText,
    entryErrorText,
    continueButtonText,
    cancelButtonText,
    placeHolderInputSocial,
    placeHolderInputPhone,
  }: any = useSelector(getCustomerVerificationContent);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const sessionId = useSelector(getSessionId);
  const clientId =
    sessionStorage.getItem(CLIENT_ID_SESSION_STORAGE_KEY) ||
    useSelector(getClientId) ||
    TRANSMIT_CONSTANTS.defaultClient;
  const analyticsButtonData = useSelector(getPiiFormData);
  const [showEntryError, setShowEntryError] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [transmitUserParams, setTransmitUserParams] = useState({
    header: {
      client_id: '',
      channel: '',
      trans_id: '',
    },
    first_name: '',
    last_name: '',
    zip_code: '',
    last_4_ssn: '',
    session_id: '',
    phone_number: '',
    user_id: '',
    device_fingerprint: '',
    language_code: '',
    app_env: '',
    branding: {},
  });
  const [formData, setFormData] = useState<CustomerVerificationFormData>({
    lastFourSocial: '',
    phoneNumber: '',
  });
  const [errors, setErrors] = useState<Partial<CustomerVerificationFormData>>(
    {},
  );
  const [showCancelModal, setShowCancelModal] = useState(false);

  // New Relic and Splunk Rum SessionId capture
  useEffect(() => {
    if (sessionId) {
      capturePageLoadAnalytics(PII_PAGE_LOAD_EVENT);

      // Passing Session Id to New Relic
      window?.newrelic?.addToTrace({ sessionId });
      window?.newrelic?.setUserId(sessionId);

      // Passing Session Id to Splunk Rum
      window?.SplunkRum?.setGlobalAttributes({
        syfToken: sessionId,
        partnerId: sessionId,
        'service.name': 'wallet-ui',
        'session.id': sessionId,
      });
    }
  }, [sessionId]);

  /************************ Transmit SDK code starts *************************/

  const handleAuth = useCallback(
    (response: TransmitResponse) => {
      setIsFormSubmitted(false);
      const {
        status: { response_code, response_desc },
        token,
      } = response;
      captureClickAnalyticsObject({
        dataReason: analyticsButtonData?.dataReason,
        dataType: analyticsButtonData?.dataType,
        dataObject: analyticsButtonData?.dataObject,
        responseCode: response_code,
        responseDesc: response_desc,
      });
      handleAuthRedirects({
        transmitResponseCode: response_code,
        transmitToken: token,
        dispatch,
        setTransmitToken,
        setShowEntryError,
        navigate,
      });
    },
    [analyticsButtonData],
  );

  /************************ Transmit SDK code ends *************************/

  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const handleCancelModalClose = (event: any) => {
    captureAnalyticsDataAttributes(event);
    setShowCancelModal(false);
  };

  const handleCancelConfirm = (event: any) => {
    captureAnalyticsDataAttributes(event);
    setShowCancelModal(false);
    navigate('/thank-you');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (/^\d*$/.test(value)) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));

      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nativeEvent = event.nativeEvent as SubmitEvent;
    const clickedButton = (nativeEvent.submitter as HTMLButtonElement) || null;
    if (clickedButton) {
      const dataReason = clickedButton.getAttribute('data-reason');
      const dataType = clickedButton.getAttribute('data-type');
      const dataObject = clickedButton.getAttribute('data-object');
      dispatch(
        setPiiFormEvent({
          dataReason: dataReason,
          dataType: dataType,
          dataObject: dataObject,
        }),
      );
    }
    const newErrors: Partial<CustomerVerificationFormData> = {};
    // Basic Validations
    const lastFourSocial = formData.lastFourSocial.toString();
    const phoneNumber = formData.phoneNumber.toString();
    if (!/^\d{4}$/.test(lastFourSocial)) {
      newErrors.lastFourSocial = lastFourSocialErrorText;
    }
    if (!/^\d{10}$/.test(phoneNumber)) {
      newErrors.phoneNumber = phoneNumberErrorText;
    }
    setErrors(newErrors);

    const params = getAuthParams({
      lastFourSocial,
      phoneNumber,
      sessionId,
      clientId,
    });

    if (Object.keys(newErrors).length === 0) {
      dispatch(addFormData(formData));
      setTransmitUserParams(params);
      setIsFormSubmitted(true);
    }
  };

  return (
    <>
      {showCancelModal && (
        <CancelModal
          isOpen={showCancelModal}
          onClose={handleCancelModalClose}
          onConfirm={handleCancelConfirm}
        />
      )}
      {isFormSubmitted && (
        <Transmit
          handleAuth={handleAuth}
          userParams={transmitUserParams}
          data-testid="transmit-container"
          className="transmit-container"
        />
      )}
      {sessionId && !isFormSubmitted && (
        <div className="customer-verification-content">
          <div className="welcome-heading">
            <h1>{header}</h1>
          </div>
          <div className="welcome-sub-heading">
            <h5>{subHeader}</h5>
          </div>
          <form
            className="customer-verification-form"
            onSubmit={handleSubmit}
            noValidate
          >
            <div className="welcome-form-input-section flex flex-col justify-center">
              {showEntryError && (
                <div className="error-message error-text-color text-center text-xs my-5">
                  {entryErrorText}
                </div>
              )}
              <div className="mb-4">
                <input
                  className={`syfinputfield input-text-field form-input ${
                    errors.lastFourSocial ? '!border-error-red-600' : ''
                  }`}
                  id="lastFourSocial"
                  type="text"
                  placeholder={placeHolderInputSocial}
                  name="lastFourSocial"
                  data-fieldname="SSN"
                  data-classname="syfinputfield"
                  maxLength={4}
                  required
                  onChange={handleChange}
                  value={formData.lastFourSocial}
                  data-testid="lastFourSocialInput"
                />
                {errors.lastFourSocial && (
                  <p className="error-txt error-text-color">
                    {errors.lastFourSocial}
                  </p>
                )}
              </div>
              <div className="mb-6">
                <input
                  className={`syfinputfield input-text-field form-input ${
                    errors.phoneNumber ? '!border-error-red-600' : ''
                  }`}
                  id="phoneNumber"
                  type="tel"
                  placeholder={placeHolderInputPhone}
                  name="phoneNumber"
                  data-fieldname="phoneNumber"
                  data-classname="syfinputfield"
                  maxLength={10}
                  required
                  onChange={handleChange}
                  value={formData.phoneNumber}
                  data-testid="phoneNumberInput"
                />
                {errors.phoneNumber && (
                  <p className="error-txt error-text-color">
                    {errors.phoneNumber}
                  </p>
                )}
              </div>
              <div className="disclaimer-text">
                <span>{disclaimerText}</span>
              </div>
              <div className="buttons-wrapper button-flex">
                <button
                  className="continue-button"
                  type="submit"
                  name="submit"
                  data-testid="submitButton"
                  data-object="button"
                  data-channel=""
                  data-type={detectDeviceType}
                  data-reason="scaPiPageContinue"
                >
                  {continueButtonText}
                </button>
              </div>
              <div className="button-flex cancel-button-flex">
                <button
                  className="button-cancel cancel-btn"
                  type="button"
                  data-object="button"
                  data-testid="cancelButton"
                  data-type={detectDeviceType}
                  data-reason="scaPiPageCancel"
                  onClick={handleCancel}
                >
                  {cancelButtonText}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default CustomerVerification;
